import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const STORAGE_KEY = "ee_auth";

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (s?.accessToken) return s;
    } catch {
      /* ignore */
    }
    return null;
  });
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const saveSession = (data) => {
    const s = {
      accessToken: data.access_token,
      idToken: data.id_token || "",
      refreshToken: data.refresh_token || "",
      expiresAt: data.expires_at,
    };
    setSession(s);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    setCustomer(data.customer);
    return data.customer;
  };

  const clearSession = () => {
    setSession(null);
    setCustomer(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const getValidToken = async () => {
    if (!session) return null;
    if (new Date(session.expiresAt) > new Date()) return session.accessToken;
    if (session.refreshToken) {
      try {
        const data = await api.authRefresh({ refresh_token: session.refreshToken });
        saveSession(data);
        return data.access_token;
      } catch {
        clearSession();
        return null;
      }
    }
    clearSession();
    return null;
  };

  useEffect(() => {
    (async () => {
      if (!session) {
        setLoading(false);
        return;
      }
      const token = await getValidToken();
      if (token) {
        try {
          setCustomer(await api.authMe(token));
        } catch {
          clearSession();
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async () => {
    const { authorize_url } = await api.authAuthorize(window.location.origin);
    window.location.href = authorize_url;
  };

  const completeLogin = async (code, state) => saveSession(await api.authCallback({ code, state }));

  const logout = async () => {
    let url = "/";
    try {
      const r = await api.authLogout(session?.idToken || "", window.location.origin);
      url = r.logout_url || "/";
    } catch {
      /* ignore */
    }
    clearSession();
    window.location.href = url;
  };

  const refresh = async () => {
    const token = await getValidToken();
    if (token) {
      try {
        setCustomer(await api.authMe(token));
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{ customer, loading, login, completeLogin, logout, refresh, getToken: getValidToken, isLoggedIn: !!customer }}
    >
      {children}
    </AuthContext.Provider>
  );
};
